import { z } from 'zod';

export const RoleSchema = z.enum([
  'super_admin',
  'developer',
  'training_admin',
  'tester',
  'learner',
]);
export type Role = z.infer<typeof RoleSchema>;

// 'pending' self-registrations can't log in until approved (or auto-
// approved via a project's allowedEmailDomains at registration time).
export const UserStatusSchema = z.enum(['active', 'pending']);
export type UserStatus = z.infer<typeof UserStatusSchema>;

export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string().min(1).max(120),
  roles: z.array(RoleSchema).min(1),
  locale: z.enum(['nl', 'en']).default('nl'),
  status: UserStatusSchema.default('active'),
  createdAt: z.string().datetime(),
});
export type User = z.infer<typeof UserSchema>;

export const LoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(200),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const RegisterRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(200),
  displayName: z.string().min(1).max(120),
  locale: z.enum(['nl', 'en']).default('nl'),
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

export const AuthResponseSchema = z.object({
  user: UserSchema,
  accessToken: z.string(),
  refreshToken: z.string(),
});
export type AuthResponse = z.infer<typeof AuthResponseSchema>;

// Registration either logs the learner straight in (bootstrap super_admin,
// or a project's allowedEmailDomains matched their email) or leaves them
// pending a training_admin/super_admin's approval — no tokens in that case.
export const RegisterResponseSchema = z.union([
  AuthResponseSchema,
  z.object({ pending: z.literal(true) }),
]);
export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;

export const ChangePasswordRequestSchema = z.object({
  currentPassword: z.string().min(8).max(200),
  newPassword: z.string().min(8).max(200),
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

// A password change revokes every other session, so it returns a fresh
// token pair to keep the tab that submitted the form logged in.
export const ChangePasswordResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
});
export type ChangePasswordResponse = z.infer<typeof ChangePasswordResponseSchema>;
